import { useEffect, useState } from "react";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import LocalStorePicker from "./LocalStorePicker";
import ExportButtons from "./ExportButtons";
import { downloadCSV, downloadPDF, fmt } from "../lib/exports";
import { useSort } from "../lib/useSort";
import { SortChips } from "./SortControls";
import PeriodBadge from "./PeriodBadge";

function fmtMoney(v) {
  const n = Number(v || 0);
  const sign = n >= 0 ? "+" : "−";
  const abs = Math.abs(n);
  if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(1)}k`;
  return `${sign}$${abs.toFixed(0)}`;
}

const RANK_LABEL = {
  momentum: "Momentum",
  revenue_delta: "Δ Revenue",
};

const RANK_HINT = {
  momentum: "Dollar delta × log(growth) — combines real revenue added with acceleration",
  revenue_delta: "Incremental dollars only — pure revenue contribution",
};

export default function EmergingProducts({ initialOutletIds }) {
  const [outletIds, setOutletIds] = useState(initialOutletIds || []);
  const [direction, setDirection] = useState("up"); // up | down
  const [windowDays, setWindowDays] = useState(30); // 30 | 60
  const [rankBy, setRankBy] = useState("momentum"); // momentum | growth | revenue_delta
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const params = { limit: 10, window_days: windowDays, direction, rank_by: rankBy };
    if (outletIds.length > 0) params.outlet_ids = outletIds.join(",");
    api.get("/products/emerging", { params })
      .then((r) => { if (!cancel) setItems(r.data || []); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [outletIds.join(","), direction, windowDays, rankBy]);

  const isUp = direction === "up";
  const Icon = isUp ? ArrowUpRight : ArrowDownRight;
  const titleColor = isUp ? "text-emerald-700" : "text-[#c72c2c]";
  const eyebrow = isUp ? "Table 2 · On the rise" : "Table 2 · Trending down";
  const heading = isUp ? "On the rise." : "Trending down.";
  const subtitle = isUp
    ? "Products with strong recent momentum · Printed Tees rolled up"
    : "Products losing traction · Printed Tees rolled up";

  const growthColor = (pct) => (pct >= 0 ? "text-emerald-700" : "text-[#c72c2c]");
  const dollarColor = (v) => (v >= 0 ? "text-emerald-700" : "text-[#c72c2c]");

  // Client-side reorder of the current top 10. Server-side ranking (Momentum
  // vs Δ Revenue) picks WHICH 10 show up; these chips reorder those 10.
  const sort = useSort("revenue_delta", "desc", {
    units: (r) => r.units_recent,
    growth: (r) => r.growth_pct,
    revenue_delta: (r) => r.revenue_delta,
  });
  const sortedItems = sort.apply(items);

  const buildExport = () => {
    const columns = [
      { key: "n", label: "#", align: "right" },
      { key: "name", label: "Product" },
      { key: "category", label: "Category" },
      { key: "units_recent", label: `Units last ${windowDays}d`, align: "right" },
      { key: "units_prev", label: `Units prior ${windowDays}d`, align: "right" },
      { key: "revenue_delta", label: "Δ Revenue", align: "right" },
      { key: "growth_pct", label: "Growth %", align: "right" },
    ];
    const flat = sortedItems.map((p, i) => ({
      n: i + 1,
      name: p.name,
      category: p.category || "",
      units_recent: fmt.int(p.units_recent),
      units_prev: fmt.int(p.units_prev),
      revenue_delta: fmt.money0(p.revenue_delta),
      growth_pct: p.growth_pct != null ? `${p.growth_pct >= 0 ? "+" : ""}${p.growth_pct}%` : "",
    }));
    return {
      filenameBase: `${isUp ? "rising" : "falling"}-products-${windowDays}d-${rankBy}`,
      meta: {
        title: isUp ? "On the rise" : "Trending down",
        subtitle: `${windowDays}d vs prior ${windowDays}d · ranked by ${RANK_LABEL[rankBy]}`,
      },
      columns,
      rows: flat,
    };
  };

  return (
    <div className="border border-[#0a0a0a] bg-[#ede8dc] p-6" data-testid="emerging-products">
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
        <div className="bm-eyebrow">{eyebrow}</div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Direction toggle */}
          <div className="flex border border-[#0a0a0a]" role="tablist" aria-label="Direction">
            <button
              onClick={() => setDirection("up")}
              data-testid="emerging-direction-up"
              className={`px-2 py-1 bm-eyebrow ${isUp ? "bg-emerald-800 text-[#f5f2eb]" : "hover:bg-[#0a0a0a]/5"}`}
            >
              Rising
            </button>
            <button
              onClick={() => setDirection("down")}
              data-testid="emerging-direction-down"
              className={`px-2 py-1 bm-eyebrow border-l border-[#0a0a0a] ${!isUp ? "bg-[#c72c2c] text-[#f5f2eb]" : "hover:bg-[#0a0a0a]/5"}`}
            >
              Falling
            </button>
          </div>

          {/* Window toggle */}
          <div className="flex border border-[#0a0a0a]">
            <button
              onClick={() => setWindowDays(30)}
              data-testid="emerging-window-30"
              className={`px-2 py-1 bm-eyebrow ${windowDays === 30 ? "bg-[#0a0a0a] text-[#f5f2eb]" : "hover:bg-[#0a0a0a]/5"}`}
            >30d vs 30d</button>
            <button
              onClick={() => setWindowDays(60)}
              data-testid="emerging-window-60"
              className={`px-2 py-1 bm-eyebrow border-l border-[#0a0a0a] ${windowDays === 60 ? "bg-[#0a0a0a] text-[#f5f2eb]" : "hover:bg-[#0a0a0a]/5"}`}
            >60d vs 60d</button>
          </div>

          {/* Rank-by toggle */}
          <div className="flex border border-[#0a0a0a]" role="tablist" aria-label="Rank by" data-testid="emerging-rankby">
            <button
              onClick={() => setRankBy("momentum")}
              data-testid="emerging-rank-momentum"
              title={RANK_HINT.momentum}
              className={`px-2 py-1 bm-eyebrow ${rankBy === "momentum" ? "bg-[#0a0a0a] text-[#f5f2eb]" : "hover:bg-[#0a0a0a]/5"}`}
            >Momentum</button>
            <button
              onClick={() => setRankBy("revenue_delta")}
              data-testid="emerging-rank-revenue"
              title={RANK_HINT.revenue_delta}
              className={`px-2 py-1 bm-eyebrow border-l border-[#0a0a0a] ${rankBy === "revenue_delta" ? "bg-[#0a0a0a] text-[#f5f2eb]" : "hover:bg-[#0a0a0a]/5"}`}
            >Δ Revenue</button>
          </div>

          <LocalStorePicker value={outletIds} onChange={setOutletIds} testId="emerging-stores" />
        </div>
      </div>
      <PeriodBadge label={`Last ${windowDays} days vs the ${windowDays} before (to today)`} note="not tied to the date picker" className="mb-2" />
      <h3 className={`bm-headline text-3xl mb-1 ${titleColor}`}>
        <Icon size={22} className="inline align-baseline -translate-y-0.5" strokeWidth={2.5} /> {heading}
      </h3>
      <p className="bm-eyebrow text-neutral-500 mb-1">{subtitle}</p>
      <p className="bm-eyebrow text-neutral-400 mb-3" data-testid="emerging-rank-hint">
        Ranked by <strong className="text-neutral-600">{RANK_LABEL[rankBy]}</strong> · {RANK_HINT[rankBy]}
      </p>

      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <SortChips
          state={sort}
          options={[
            { k: "units", label: "Units" },
            { k: "growth", label: "Δ" },
            { k: "revenue_delta", label: "Revenue" },
          ]}
          testIdPrefix="emerging-sort"
        />
        <ExportButtons
          disabled={!items.length}
          testIdPrefix="emerging-export"
          onCSV={() => { const e = buildExport(); downloadCSV(e.filenameBase, e.columns, e.rows); }}
          onPDF={() => { const e = buildExport(); downloadPDF(e.filenameBase, e.meta, e.columns, e.rows); }}
        />
      </div>

      {loading && items.length === 0 && <div className="text-sm text-neutral-500">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="text-sm text-neutral-600">
          {isUp ? "Nothing hit the threshold this window." : "Nothing declined enough this window."}
        </div>
      )}

      <div className="space-y-2">
        {sortedItems.map((p, i) => (
          <div key={p.product_id || p.name || i} className="border-t border-[#0a0a0a] pt-2" data-testid={`emerging-row-${i}`}>
            <div className="flex items-baseline justify-between gap-3">
              <div className="flex items-baseline gap-2 flex-1 min-w-0">
                <span className="bm-eyebrow text-neutral-500 w-8">#{String(i + 1).padStart(2, "0")}</span>
                <div className="min-w-0">
                  <div className="font-medium truncate">
                    {p.rollup
                      ? <Link to="/tees" className="text-[#c72c2c] hover:underline" data-testid={`emerging-tees-link-${i}`}>{p.name} <span className="bm-eyebrow">· view designs →</span></Link>
                      : p.name}
                  </div>
                  <div className="bm-eyebrow text-neutral-500 mt-0.5">
                    <span className="hidden md:inline">{p.category} &middot; </span>last {windowDays}d {p.units_recent}u vs prior {windowDays}d {p.units_prev}u
                  </div>
                </div>
              </div>
              <div className="text-right whitespace-nowrap shrink-0">
                <div className={`font-mono-bm text-sm font-semibold ${dollarColor(p.revenue_delta)}`}>
                  {fmtMoney(p.revenue_delta)}
                </div>
                <div className={`font-mono-bm text-[11px] ${growthColor(p.growth_pct)}`}>
                  {p.growth_pct >= 0 ? "+" : ""}{p.growth_pct}%
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
